import type { Field } from '@atlas/react-forms';
import { useCallback } from 'react';

import {
  type AsyncValidationResponse,
  type ErrorViolation,
  asyncValidationRequest,
} from 'api/forms';

type AsyncValidationHook = {
  validateField: (field: Field) => Promise<string[]>;
};

const useAsyncValidation = (
  formId: string | undefined,
): AsyncValidationHook => {
  const validateField = useCallback(
    async (field: Field) => {
      let errors: ErrorViolation[] = [];

      const { disabled, name, value } = field;

      if (
        formId === undefined ||
        disabled ||
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.length === 0)
      ) {
        return [];
      }

      try {
        await asyncValidationRequest({
          body: {
            formId: formId,
            fieldName: name,
            value: String(value),
          },
        });
      } catch (error) {
        /**
         * in this case the error may be related to the server.
         * In this case we expect the server to return the correct data.
         * But here another type of error may also occur,
         * for example a connection error with the server
         */
        const typedError = error as AsyncValidationResponse;

        const newErrors = typedError.errors[0].violations;
        if (newErrors?.length) {
          errors = newErrors;
        }
      }

      // we only need to return an array of error messages here
      return errors.map((error) => error.message);
    },
    [formId],
  );

  return { validateField };
};

export default useAsyncValidation;
